import { useState, useEffect, useCallback } from "react";
import { fetchRules, deleteRule, disableRule } from "../../api/client";
import type { ParserRuleRecord } from "../../api/client";
import { Trash2, XCircle, Zap, ShieldCheck } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { prettyFieldLabel, prettyFileType } from "../../utils/labels";
import { useNavigate } from "react-router-dom";

export default function RulesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rules, setRules] = useState<ParserRuleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user?.is_admin ?? false;

  useEffect(() => {
    if (!isAdmin) {
      navigate("/");
      return;
    }
    loadRules();
  }, [isAdmin, navigate]);

  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRules();
      setRules(data);
    } catch (err: any) {
      setError(err.message || "Failed to load rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(
    async (ruleId: string) => {
      try {
        await deleteRule(ruleId);
        setRules((prev) => prev.filter((r) => r.id !== ruleId));
      } catch {}
    },
    [],
  );

  const handleDisable = useCallback(
    async (ruleId: string) => {
      try {
        await disableRule(ruleId);
        setRules((prev) => prev.filter((r) => r.id !== ruleId));
      } catch {}
    },
    [],
  );

  if (!isAdmin) return null;

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck className="w-6 h-6 text-forest-600" />
        <h1 className="font-display text-xl font-semibold text-bark-800">
          Parser Rules
        </h1>
        <span className="text-sm text-bark-400">
          {rules.length} rule{rules.length !== 1 ? "s" : ""} active
        </span>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-forest-600 border-t-transparent" />
        </div>
      )}

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && rules.length === 0 && (
        <div className="text-center py-12 text-bark-400">
          <Zap className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No parser rules have been created yet.</p>
          <p className="text-xs mt-1">
            Rules are promoted from repeated corrections in the verification UI.
          </p>
        </div>
      )}

      {!loading && rules.length > 0 && (
        <div className="border border-grove-300 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-grove-50 border-b border-grove-200">
                <th className="text-left px-4 py-3 font-medium text-bark-600">Rule</th>
                <th className="text-left px-4 py-3 font-medium text-bark-600">Type</th>
                <th className="text-left px-4 py-3 font-medium text-bark-600">File Type</th>
                <th className="text-right px-4 py-3 font-medium text-bark-600">Hits</th>
                <th className="text-left px-4 py-3 font-medium text-bark-600">Created</th>
                <th className="text-right px-4 py-3 font-medium text-bark-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr
                  key={rule.id}
                  className="border-b border-grove-100 hover:bg-grove-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-bark-700">
                      {rule.display_name}
                    </div>
                    {rule.source_correction_ids &&
                      rule.source_correction_ids.length > 0 && (
                        <div className="text-xs text-bark-400 mt-0.5">
                          From {rule.source_correction_ids.length} correction
                          {rule.source_correction_ids.length !== 1 ? "s" : ""}
                        </div>
                      )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-sage-100 text-sage-600">
                      {prettyFieldLabel(rule.rule_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-bark-500">
                    {prettyFileType(rule.file_type)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-bark-700">
                    {rule.hit_count}
                  </td>
                  <td className="px-4 py-3 text-bark-400 text-xs">
                    {new Date(rule.approved_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleDisable(rule.id)}
                        className="p-1.5 rounded-md text-bark-400 hover:text-honey-600 hover:bg-honey-50 transition-colors"
                        title="Disable rule"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(rule.id)}
                        className="p-1.5 rounded-md text-bark-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete rule"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
